import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AppState } from '@store/state';
import { format } from 'date-fns';
import { Observable, Subject } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import {
  AirportMapped,
  EditFlightLeg,
  EditFlightResponseMapped,
  EditFlightLegWithFormFields,
} from '../flight/_interfaces_/booked-flight.mapped.interface';
import { LocalStorageService } from '../local-storage/local-storage.service';
import { UrlService } from '../url/url.service';
import { AirportOptions, AirportsResponse, SearchFormLegCriteriaValues, SearchFormValues, SearchRequestBody } from './_interfaces';
import { FlightChangeDetails, Leg } from '../option-order/_interfaces_/option-order-details.mapped.interface';
import { DateFormatEnum } from '@enums/date-format.enum';
import { DateService } from '../date/date.service';
import { CancelInfo, ChangeFlightDetails, FlightChangeLeg } from '@pages/common-order/_interfaces_/flight-booking-action.interface';

@Injectable({
  providedIn: 'root',
})
export class SearchFormService {
  public newSearch$ = new Subject<boolean>();

  constructor(
    @Inject(FormBuilder) private fb: FormBuilder,
    @Inject(UrlService) private _urlService: UrlService,
    @Inject(HttpClient) private httpClient: HttpClient,
    @Inject(Router) private _router: Router,
    @Inject(Store) private _store: Store<AppState>,
    @Inject(LocalStorageService) private _localStorageService: LocalStorageService,
    @Inject(DateService) private _dateService: DateService
  ) {}

  public createSearchFormStructure(searchData: EditFlightResponseMapped): FormGroup {
    return this.fb.group({
      flightIdentifier: searchData.identifier,
      legCriteriaList: this.createLegForm(searchData.flightLeg),
    });
  }

  private createLegForm(legData: EditFlightLeg[]): FormArray {
    return this.fb.array(
      legData.map((leg: EditFlightLegWithFormFields) =>
        this.fb.group({
          wasLegChanged: leg.wasLegChanged ?? false,
          legNumber: leg.legNumber,
          departureAirportName: [leg.departureAirport?.displayName || leg.departureAirportName, Validators.required],
          departureCode: [leg.departureAirport?.code || leg.departureCode, Validators.required],
          arrivalAirportName: [leg.arrivalAirport?.displayName || leg.arrivalAirportName, Validators.required],
          arrivalCode: [leg.arrivalAirport?.code || leg.arrivalCode, Validators.required],
          departureDate: [new Date(leg.departureDate), Validators.required],
          cabinClass: leg.cabinClass,
          nonStop: leg.nonStop ?? false,
        })
      )
    );
  }

  public getAirportsRequest(query: string, targetType: string): Observable<AirportOptions[]> {
    const url = this._urlService.parseDynamicUrl('AIRPORTS_URL', { query, targetType });
    return this.httpClient.get<AirportsResponse>(url).pipe(map((response: AirportsResponse) => response.Options));
  }

  public prepareSearchPayload(searchFlightData: SearchFormValues): void {
    this.newSearch$.next(true);
    const payload: SearchRequestBody = this.mapPostFlightSearchPayload(searchFlightData);
    this._localStorageService.setFlightSearchPayload(payload);
    this._router.navigate(['/result']);
  }

  public mapPostFlightSearchPayload(searchFormData: SearchFormValues): SearchRequestBody {
    return {
      FlightIdentifier: searchFormData.flightIdentifier,
      SelectedLegs: searchFormData.legCriteriaList
        .filter((leg: SearchFormLegCriteriaValues) => leg.wasLegChanged)
        .map((leg: SearchFormLegCriteriaValues) => {
          return {
            LegNumber: leg.legNumber,
            DepartureCode: leg.departureCode,
            ArrivalCode: leg.arrivalCode,
            DepartureDate: this._dateService.formatDate(leg.departureDate, DateFormatEnum.yyyyMMdd),
            CabinClass: leg.cabinClass ? leg.cabinClass : null,
            NonstopOnly: leg.nonStop ?? false,
          };
        }),
    };
  }

  public mapSearchDataForStore(searchFormValues: SearchFormValues): EditFlightResponseMapped {
    return {
      identifier: searchFormValues.flightIdentifier,
      flightLeg: searchFormValues.legCriteriaList.map((leg: SearchFormLegCriteriaValues) => {
        return {
          legNumber: leg.legNumber,
          departureAirport: {
            code: leg.departureCode,
            displayName: leg.departureAirportName,
          } as AirportMapped,
          arrivalAirport: {
            code: leg.arrivalCode,
            displayName: leg.arrivalAirportName,
          } as AirportMapped,
          departureDate: this._dateService.formatDate(leg.departureDate, DateFormatEnum.yyyyMMdd),
          cabinClass: leg.cabinClass,
          nonStop: leg.nonStop,
        };
      }),
    } as EditFlightResponseMapped;
  }

  public mapOptionOrderSearchDataForStore(searchData: FlightChangeDetails): EditFlightResponseMapped {
    return {
      identifier: searchData.flightChangeIdentifier,
      flightLeg: searchData.changeFlightDetails?.legs.map((leg: Leg) => {
        return {
          legNumber: leg.legNumber,
          departureAirport: leg.departureAirport,
          arrivalAirport: leg.arrivalAirport,
          departureDate: this._dateService.formatDate(leg.departureDate, DateFormatEnum.yyyyMMdd),
          cabinClass: leg.cabinClass,
          nonStop: leg.nonStop,
          departureTime: leg.departureTime,
        };
      }),
    } as EditFlightResponseMapped;
  }

  public mapCommonOrderSearchDataForStore(
    cancelInfo: CancelInfo,
    flightDetails: ChangeFlightDetails,
    flightIdentifier: string,
    pnr: string
  ): EditFlightResponseMapped {
    return {
      pnr: pnr,
      identifier: flightIdentifier,
      flightLeg: flightDetails?.legs?.map((leg: FlightChangeLeg) => {
        return {
          legNumber: leg.legNumber,
          departureAirport: leg.departureAirport,
          arrivalAirport: leg.arrivalAirport,
          departureDate: this._dateService.formatDate(leg.departureDate, DateFormatEnum.yyyyMMdd),
          cabinClass: leg.cabinClass,
          nonStop: leg.nonStop,
          departureTime: leg.departureTime,
        };
      }),
      cancelInfo: cancelInfo,
    } as EditFlightResponseMapped;
  }

  public markAsTouchedAndValidateAll(formGroup: FormGroup | FormArray): Observable<string | 'VALID' | 'INVALID' | 'PENDING' | 'DISABLED'> {
    formGroup.markAsTouched({ onlySelf: true });
    formGroup.updateValueAndValidity({ onlySelf: true });

    this.traverseForm(formGroup);

    return formGroup.statusChanges.pipe(startWith(formGroup.status));
  }

  private traverseForm(form: FormGroup | FormArray): void {
    Object.values(form.controls).forEach((control: AbstractControl) => {
      if (control.enabled) {
        if (control instanceof FormGroup || control instanceof FormArray) {
          this.traverseForm(control);
        } else {
          control.markAsTouched({ onlySelf: true });
          control.updateValueAndValidity({ onlySelf: true });
        }
      }
    });
  }
}
